import React, { useState } from 'react';

const FIELDS = [
  { key: 'student_name', label: 'Full name', required: true },
  { key: 'email', label: 'Email', required: true, type: 'email' },
  { key: 'phone', label: 'Phone', required: true },
  { key: 'dob', label: 'Date of birth', required: true, type: 'date' },
  { key: 'marital_status', label: 'Marital status' },
  { key: 'field_of_study', label: 'Field of study' },
  { key: 'preferred_location', label: 'Preferred location' },
  { key: 'high_school_country', label: 'High school country' },
  { key: 'emergency_contact_name', label: 'Emergency contact name' },
  { key: 'emergency_contact_relation', label: 'Emergency contact relation' },
  { key: 'emergency_contact_phone', label: 'Emergency contact phone' },
];

/** Shared editable-profile form used by the recruiter portal and the student
 *  portal. `onSave(payload)` must return {ok, error} — caller does the fetch. */
export default function ProfileEditForm({ initial, onSave, onCancel, testIdPrefix = 'profile-edit' }) {
  const [form, setForm] = useState(() => {
    const f = {};
    FIELDS.forEach(({ key }) => { f[key] = initial?.[key] || ''; });
    const q = initial?.qualifications;
    f.qualifications = typeof q === 'string' ? q : q?.summary || '';
    return f;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    for (const { key, label, required } of FIELDS) {
      if (required && !String(form[key] || '').trim()) {
        setError(`${label} is required.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.qualifications.trim()) delete payload.qualifications;
      const res = await onSave(payload);
      if (!res.ok) setError(res.error || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-form`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map(({ key, label, type }) => (
          <div key={key}>
            <label className="text-[11px] uppercase tracking-wider text-tge-muted font-semibold">{label}</label>
            <input
              type={type || 'text'}
              value={form[key]}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              className="mt-1 w-full border border-tge-navy/15 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-tge-navy/40"
              data-testid={`${testIdPrefix}-${key}`}
            />
          </div>
        ))}
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-tge-muted font-semibold">Academic background / qualifications</label>
        <textarea
          rows={3}
          value={form.qualifications}
          onChange={(e) => setForm((f) => ({ ...f, qualifications: e.target.value }))}
          className="mt-1 w-full border border-tge-navy/15 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-tge-navy/40 resize-none"
          data-testid={`${testIdPrefix}-qualifications`}
        />
      </div>
      {error && <div className="text-xs text-rose-600" data-testid={`${testIdPrefix}-error`}>{error}</div>}
      <div className="flex items-center gap-2">
        <button
          onClick={submit}
          disabled={saving}
          className="text-xs font-semibold bg-tge-navy hover:bg-tge-navy/90 disabled:opacity-50 text-white rounded-lg px-4 py-2 transition"
          data-testid={`${testIdPrefix}-save`}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          onClick={onCancel}
          className="text-xs font-semibold border border-tge-navy/20 text-tge-navy rounded-lg px-4 py-2 hover:bg-tge-cream-soft transition"
          data-testid={`${testIdPrefix}-cancel`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
